/** Pull latest cloud snapshot into a local project (open + before-commit). */

import { invoke } from "@tauri-apps/api/core";
import { listen, type UnlistenFn } from "@tauri-apps/api/event";
import { getCloudSession, hasCloudBackupEntitlement } from "./cloudAuth";
import { isTauri } from "./runtime";
import type { ZwCloudSync, ZwConfig } from "./zwConfig";
import { withCloudActivity } from "./cloudActivity";
import {
  downloadSnapshotPack,
  ensureCloudProject,
  listSnapshots,
  packsNeededAfter,
  rememberCloudProjectId,
  snapshotApplyChain,
  type DownloadedPack,
  type SnapshotMeta,
} from "./cloudSnapshot";
import { atIndexMaybe, isJust, isNothing } from "./maybe";
import { trimmedOrEmpty, trimmedOrNull } from "./trimmedString";

export type SyncReason = "open" | "precommit" | "realtime";

/** Known cloud project / snapshot from a channel join or `snapshot_created`. */
export type CloudSnapshotHint = {
  cloudProjectId: string;
  snapshotId?: string | null;
  gitHeadOid?: string | null;
  gitBaseOid?: string | null;
};

export type SyncFromCloudResult = {
  status:
    | "skipped"
    | "unchanged"
    | "local_ahead"
    | "fast_forwarded"
    | "resolved_keep_local"
    | "resolved_use_cloud"
    | "diverged";
  headOid?: string | null;
};

type ApplyBundleResult = {
  status: string;
  headOid?: string | null;
  localOid?: string | null;
  cloudOid?: string | null;
};

type BeforeCommitPayload = {
  root: string;
  requestId: string;
};

async function cloudBackupActive(): Promise<boolean> {
  return hasCloudBackupEntitlement();
}

async function readConfig(root: string): Promise<ZwConfig | null> {
  try {
    return await invoke<ZwConfig>("read_zw_config", { rootPath: root });
  } catch {
    return null;
  }
}

async function projectDisplayName(root: string): Promise<string> {
  const config = await readConfig(root);
  const title = trimmedOrNull(config?.metadata?.title);
  if (title !== null) return title;
  const parts = root.split(/[/\\]/).filter((value) => value.length > 0);
  const lastM = atIndexMaybe(parts, parts.length - 1);
  if (isJust(lastM) && lastM.value.length > 0) return lastM.value;
  return "Project";
}

/** Cloud API project id for a local project root, or null when unavailable. */
export async function resolveCloudProjectId(
  root: string,
): Promise<string | null> {
  if ((await cloudBackupActive()) === false) return null;
  const config = await readConfig(root);
  const clientProjectId = trimmedOrNull(config?.cloudProjectId);
  if (clientProjectId === null) return null;
  try {
    return await ensureCloudProject(
      clientProjectId,
      await projectDisplayName(root),
    );
  } catch (error) {
    console.warn("[cloudSync] resolve cloud project failed", error);
    return null;
  }
}

async function downloadPacks(
  cloudProjectId: string,
  localOid: string | null,
  hint?: CloudSnapshotHint,
  listed?: SnapshotMeta[],
): Promise<DownloadedPack[]> {
  const hintedSnapshotId = trimmedOrEmpty(hint?.snapshotId);
  const hintedBase = trimmedOrNull(hint?.gitBaseOid);
  const canSingle =
    hintedSnapshotId.length > 0 &&
    (hintedBase === null || hintedBase === localOid);

  if (canSingle) {
    return [await downloadSnapshotPack(cloudProjectId, hintedSnapshotId)];
  }

  const snapshots = listed ?? (await listSnapshots(cloudProjectId));
  const firstId = snapshots[0]?.id;
  const chainTarget =
    trimmedOrEmpty(hint?.snapshotId).length > 0 ? hint?.snapshotId : firstId;
  const chain = snapshotApplyChain(snapshots, chainTarget);
  let needed = packsNeededAfter(chain, localOid);
  const packs: DownloadedPack[] = [];

  const newestHead = trimmedOrNull(snapshots[0]?.gitHeadOid);
  if (
    needed.length === 0 &&
    newestHead !== null &&
    localOid !== null &&
    localOid.length > 0
  ) {
    const newestM = atIndexMaybe(snapshots, 0);
    if (isJust(newestM)) {
      const newest = newestM.value;
      const cloudTip = trimmedOrNull(newest.gitHeadOid);
      if (cloudTip !== null && cloudTip !== localOid) {
        const base = trimmedOrNull(newest.gitBaseOid);
        if (base === null || base === localOid) {
          needed = [newest];
        } else {
          console.warn(
            "[cloudSync] cloud tip ahead of local but pack chain is empty",
            { localOid, cloudTip, gitBaseOid: newest.gitBaseOid },
          );
        }
      }
    }
  }

  for (const snap of needed) {
    packs.push(await downloadSnapshotPack(cloudProjectId, snap.id));
  }
  return packs;
}

type DivergenceChoice = "keep_local" | "use_cloud";

let divergencePrompt: ((cloudOid: string) => Promise<DivergenceChoice>) | null =
  null;

/** Register UI for the ask-once divergence dialog (App / Editor). */
export function registerCloudDivergencePrompt(
  fn: ((cloudOid: string) => Promise<DivergenceChoice>) | null,
): void {
  divergencePrompt = fn;
}

async function askDivergence(cloudOid: string): Promise<DivergenceChoice> {
  const promptNow = divergencePrompt;
  if (promptNow !== null) return promptNow(cloudOid);

  // App.vue registers the prompt on mount; wait briefly so open-sync racing
  // the first paint still gets the real dialog (not window.confirm).
  for (let i = 0; i < 40; i++) {
    await new Promise((r) => setTimeout(r, 25));
    const prompt = divergencePrompt;
    if (prompt !== null) return prompt(cloudOid);
  }

  // Last resort — avoid window.confirm on mobile WebViews (unreliable / silent).
  const mobile =
    typeof document !== "undefined" &&
    document.documentElement.dataset.zwMobileShell === "1";
  if (
    mobile === false &&
    typeof window !== "undefined" &&
    "confirm" in window
  ) {
    const useCloud = window.confirm(
      "This project was edited on another device.\n\nOK = use the cloud version\nCancel = keep this computer’s version",
    );
    return useCloud ? "use_cloud" : "keep_local";
  }
  // Mobile without a registered prompt: prefer cloud so a stale hollow local
  // copy cannot silently win.
  return "use_cloud";
}

async function patchCloudSync(
  root: string,
  patch: {
    lastUploadedOid?: string | null;
    lastPulledOid?: string | null;
    divergenceCloudOid?: string | null;
    divergenceChoice?: string | null;
    clearDivergence?: boolean;
    lastUploadedChronicleOid?: string | null;
    lastPulledChronicleOid?: string | null;
  },
): Promise<void> {
  await invoke("patch_cloud_sync", {
    rootPath: root,
    lastUploadedOid: patch.lastUploadedOid,
    lastPulledOid: patch.lastPulledOid,
    divergenceCloudOid: patch.divergenceCloudOid,
    divergenceChoice: patch.divergenceChoice,
    clearDivergence: patch.clearDivergence ?? false,
    lastUploadedChronicleOid: patch.lastUploadedChronicleOid,
    lastPulledChronicleOid: patch.lastPulledChronicleOid,
  });
}

function rememberedChoice(
  sync: ZwCloudSync | null,
  cloudOid: string,
): DivergenceChoice | null {
  const d = sync?.divergence;
  if (d === null || typeof d === "undefined" || d.cloudOid !== cloudOid) {
    return null;
  }
  return d.choice;
}

function nonEmptyOid(value: string | null): string {
  return trimmedOrEmpty(value);
}

/**
 * Pull the latest cloud snapshot into `root` when entitled and signed in.
 * Fast-forwards when safe; asks once on divergence.
 *
 * Pass `hint` from a channel event to skip listing projects/snapshots.
 */
export async function syncFromCloud(
  root: string,
  _reason: SyncReason = "open",
  hint?: CloudSnapshotHint,
): Promise<SyncFromCloudResult> {
  if (isTauri() === false) return { status: "skipped" };
  if ((await cloudBackupActive()) === false) return { status: "skipped" };

  const result = await withCloudActivity(root, () =>
    syncFromCloudInner(root, hint),
  );
  // Deliberately independent of (and never awaited into) the result above —
  // the chronicle (#105 Phase 1) has its own fast-forward-or-leave-alone
  // logic and must never affect, delay, or fail the manuscript sync path
  // that produced `result`.
  void syncChronicleFromCloud(root, hint);
  return result;
}

/**
 * Best-effort, independent chronicle pull: re-lists snapshots (a second,
 * small request — deliberately not threaded through `syncFromCloudInner`'s
 * divergence-reconciliation logic above, which exists solely for the
 * manuscript branch) and, if the newest one carries a chronicle oid this
 * device doesn't have, applies it. A non-fast-forward result just means this
 * device has local chronicle commits not yet uploaded — left alone; the next
 * upload pushes them for `zw-cloud` to merge, and a later pull fast-forwards
 * cleanly once that's landed.
 */
async function syncChronicleFromCloud(
  root: string,
  hint?: CloudSnapshotHint,
): Promise<void> {
  try {
    const hintedId = trimmedOrNull(hint?.cloudProjectId);
    const cloudId =
      hintedId !== null ? hintedId : await resolveCloudProjectId(root);
    if (cloudId === null || cloudId.length === 0) return;

    const listed = await listSnapshots(cloudId);
    const newestM = atIndexMaybe(listed, 0);
    if (isNothing(newestM)) return;
    const newest = newestM.value;
    const newestChronicle = trimmedOrNull(newest.chronicleHeadOid);
    if (newestChronicle === null) return;

    const pack = await downloadSnapshotPack(cloudId, newest.id);
    const packChronicle = trimmedOrNull(pack.chronicleHeadOid);
    if (packChronicle === null) return;

    await invoke("apply_chronicle_bundle", {
      rootPath: root,
      bundleBase64: pack.bundleBase64,
      chronicleHeadOid: packChronicle,
    });
    await patchCloudSync(root, {
      lastPulledChronicleOid: packChronicle,
    });
  } catch (error) {
    console.warn("[cloudSync] chronicle pull failed", error);
  }
}

async function syncFromCloudInner(
  root: string,
  hint?: CloudSnapshotHint,
): Promise<SyncFromCloudResult> {
  const config = await readConfig(root);
  const clientProjectId = trimmedOrNull(config?.cloudProjectId);
  const hintProjectId = trimmedOrNull(hint?.cloudProjectId);
  if (clientProjectId === null && hintProjectId === null) {
    return { status: "skipped" };
  }

  let cloudId = trimmedOrEmpty(hint?.cloudProjectId);
  if (cloudId.length > 0 && clientProjectId !== null) {
    rememberCloudProjectId(clientProjectId, cloudId);
  }
  if (cloudId.length === 0) {
    if (clientProjectId === null) return { status: "skipped" };
    try {
      cloudId = await ensureCloudProject(
        clientProjectId,
        await projectDisplayName(root),
      );
    } catch (error) {
      console.warn("[cloudSync] ensure project failed", error);
      return { status: "skipped" };
    }
  }

  const hintedOid = trimmedOrEmpty(hint?.gitHeadOid);

  const localOid = await invoke<string | null>("git_head_oid", {
    rootPath: root,
  }).catch(() => null);

  let listed: SnapshotMeta[];
  try {
    listed = await listSnapshots(cloudId);
  } catch (error) {
    console.warn("[cloudSync] list snapshots failed", error);
    return { status: "skipped" };
  }

  const cloudTip = trimmedOrNull(listed[0]?.gitHeadOid);

  if (
    hintedOid.length > 0 &&
    localOid !== null &&
    localOid.length > 0 &&
    hintedOid === localOid
  ) {
    return { status: "unchanged", headOid: localOid };
  }

  if (
    cloudTip !== null &&
    localOid !== null &&
    localOid.length > 0 &&
    cloudTip === localOid
  ) {
    return { status: "unchanged", headOid: localOid };
  }

  let packs: DownloadedPack[];
  try {
    packs = await downloadPacks(cloudId, localOid, hint, listed);
  } catch (error) {
    console.warn("[cloudSync] download failed", error);
    return { status: "skipped" };
  }

  if (packs.length === 0) {
    return { status: "unchanged", headOid: localOid };
  }

  const lastPackHead = trimmedOrNull(packs[packs.length - 1]?.gitHeadOid);
  const cloudOid = hintedOid.length > 0 ? hintedOid : (lastPackHead ?? "");

  if (
    cloudOid.length > 0 &&
    localOid !== null &&
    localOid.length > 0 &&
    cloudOid === localOid
  ) {
    return { status: "unchanged", headOid: localOid };
  }

  const remembered = rememberedChoice(config?.cloudSync ?? null, cloudOid);
  if (remembered === "keep_local") {
    return { status: "resolved_keep_local", headOid: localOid };
  }

  const applyPack = async (
    downloaded: DownloadedPack,
    resolution?: DivergenceChoice,
  ): Promise<ApplyBundleResult> => {
    const tip =
      trimmedOrNull(downloaded.gitHeadOid) ??
      (cloudOid.length > 0 ? cloudOid : null);
    if (tip === null || tip.length === 0) {
      throw new Error("Cloud snapshot is missing its tip commit id");
    }
    return invoke<ApplyBundleResult>("apply_cloud_bundle", {
      rootPath: root,
      bundleBase64: downloaded.bundleBase64,
      gitHeadOid: tip,
      resolution: resolution ?? null,
    });
  };

  const applyAll = async (
    resolution?: DivergenceChoice,
  ): Promise<ApplyBundleResult> => {
    let result: ApplyBundleResult | null = null;
    for (const pack of packs) {
      result = await applyPack(pack, resolution);
      if (result.status === "diverged") return result;
    }
    if (result === null) {
      throw new Error("Cloud sync apply produced no result");
    }
    return result;
  };

  let result: ApplyBundleResult;
  try {
    result =
      remembered === "use_cloud"
        ? await applyAll("use_cloud")
        : await applyAll();
  } catch (error) {
    console.warn("[cloudSync] apply failed, retrying snapshot chain", error);
    try {
      const listed = await listSnapshots(cloudId);
      const chain = snapshotApplyChain(listed, hint?.snapshotId);
      packs = [];
      for (const snap of chain) {
        packs.push(await downloadSnapshotPack(cloudId, snap.id));
      }
      result =
        remembered === "use_cloud"
          ? await applyAll("use_cloud")
          : await applyAll();
    } catch (retryError) {
      console.warn("[cloudSync] apply failed", retryError);
      return { status: "skipped" };
    }
  }

  if (result.status === "diverged") {
    const divergedCloudOid = nonEmptyOid(result.cloudOid ?? null);
    const choice = await askDivergence(
      divergedCloudOid.length > 0 ? divergedCloudOid : cloudOid,
    );
    try {
      result = await applyAll(choice);
    } catch (error) {
      console.warn("[cloudSync] resolve failed", error);
      return { status: "diverged", headOid: localOid };
    }
    const patchCloudOid = nonEmptyOid(result.cloudOid ?? null);
    await patchCloudSync(root, {
      divergenceCloudOid: patchCloudOid.length > 0 ? patchCloudOid : cloudOid,
      divergenceChoice: choice,
    });
  }

  if (
    result.status === "fast_forwarded" ||
    result.status === "resolved_use_cloud" ||
    result.status === "unchanged" ||
    result.status === "local_ahead"
  ) {
    const headOid = trimmedOrNull(result.headOid);
    if (headOid !== null) {
      await patchCloudSync(root, {
        lastPulledOid: headOid,
        clearDivergence: result.status !== "local_ahead",
      });
    }
  }

  return {
    status: result.status as SyncFromCloudResult["status"],
    headOid: result.headOid ?? null,
  };
}

let beforeCommitUnlisten: UnlistenFn | null = null;

/**
 * When Rust requests a commit, pull from cloud first, then finish the flush.
 */
export async function installCloudSyncBeforeCommitListener(): Promise<
  () => void
> {
  if (isTauri() === false) return () => {};
  if (beforeCommitUnlisten !== null) {
    return () => {
      beforeCommitUnlisten?.();
      beforeCommitUnlisten = null;
    };
  }

  beforeCommitUnlisten = await listen<BeforeCommitPayload>(
    "cloud-sync-before-commit",
    (event) => {
      const root = event.payload?.root;
      const requestId = event.payload?.requestId;
      if (
        root === null ||
        root.length === 0 ||
        requestId === null ||
        requestId.length === 0
      ) {
        return;
      }
      void (async () => {
        try {
          if (getCloudSession() !== null) {
            await syncFromCloud(root, "precommit");
          }
        } catch (error) {
          console.warn("[cloudSync] pre-commit sync failed", error);
        } finally {
          try {
            await invoke("finish_cloud_sync_before_commit", { requestId });
          } catch (error) {
            console.warn("[cloudSync] finish commit failed", error);
          }
        }
      })();
    },
  );

  return () => {
    beforeCommitUnlisten?.();
    beforeCommitUnlisten = null;
  };
}
